<template>
  <el-dialog
    :visible.sync="innerVisible"
    :before-close="handleBeforeClose"
    custom-class="styleFormDialogShell collectionModule"
    append-to-body
    :close-on-click-modal="false"
    :show-close="true"
    width="580px"
    top="6vh"
  >
    <div slot="title" class="styleFormDialogShell__header">
      <h2 class="styleFormDialogShell__title">{{ dialogTitle }}</h2>
      <span class="styleFormDialogShell__badge" :class="modeBadgeClass">{{ modeBadgeText }}</span>
    </div>

    <div v-loading="loading" class="styleFormDialogShell__scroll">
      <template v-if="isReadonly">
        <el-card class="styleFormCard" shadow="never">
          <div slot="header">
            <strong class="styleFormCard__title">基本信息</strong>
          </div>
          <div class="styleFormReadonlyGrid">
            <div class="styleFormReadonlyGrid__full">
              <span class="styleFormReadonlyItem__label">样式名称</span>
              <span class="styleFormReadonlyItem__value">{{ ruleForm.name || '—' }}</span>
            </div>
            <div>
              <span class="styleFormReadonlyItem__label">样式类型</span>
              <span
                class="styleFormReadonlyItem__value styleFormReadonlyItem__value--mono"
              >{{ ruleForm.styleType || '—' }}</span>
            </div>
            <div>
              <span class="styleFormReadonlyItem__label">网格</span>
              <span
                class="styleFormReadonlyItem__value styleFormReadonlyItem__value--mono"
              >{{ formatGridSize(ruleForm.columNum) }}</span>
            </div>
          </div>
        </el-card>

        <el-card class="styleFormCard" shadow="never">
          <div slot="header">
            <strong class="styleFormCard__title">展示配置</strong>
          </div>
          <div class="styleFormReadonlyGrid">
            <div>
              <span class="styleFormReadonlyItem__label">展示方式</span>
              <span class="styleFormReadonlyItem__value">{{
                getDisplayModeLabel(ruleForm.displayMode)
              }}</span>
            </div>
            <div>
              <span class="styleFormReadonlyItem__label">标题显示</span>
              <span
                class="styleFormReadonlyPill"
                :class="
                  isShowTitleEnabled(ruleForm.isShowTitle)
                    ? 'styleFormReadonlyPill--on'
                    : 'styleFormReadonlyPill--off'
                "
              >
                {{ getShowTitleLabel(ruleForm.isShowTitle) }}
              </span>
            </div>
          </div>
        </el-card>
      </template>

      <el-form
        v-else
        ref="ruleForm"
        :model="ruleForm"
        :rules="rules"
        label-position="top"
        size="small"
        hide-required-asterisk
        @submit.native.prevent
      >
        <el-card class="styleFormCard" shadow="never">
          <div slot="header">
            <strong class="styleFormCard__title">基本信息</strong>
          </div>
          <div class="styleFormField">
            <span class="styleFormField__label styleFormField__label--required">样式名称</span>
            <el-form-item prop="name" label-width="0" class="styleFormField__item">
              <el-input v-model="ruleForm.name" placeholder="请输入样式名称" />
            </el-form-item>
          </div>
          <div class="styleFormField">
            <span class="styleFormField__label styleFormField__label--required">样式类型</span>
            <el-form-item prop="styleType" label-width="0" class="styleFormField__item">
              <el-select
                v-model="ruleForm.styleType"
                placeholder="请选择或输入样式类型"
                popper-class="linkListFilterDropdown"
                filterable
                allow-create
                default-first-option
                style="width: 100%"
              >
                <el-option
                  v-for="item in styleTypeOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </div>
        </el-card>

        <el-card class="styleFormCard" shadow="never">
          <div slot="header">
            <strong class="styleFormCard__title">展示配置</strong>
          </div>
          <p class="styleFormCard__hint">决定页面组件如何排列资源，以及是否展示区块标题。</p>
          <div class="styleFormConfigGrid">
            <div class="styleFormField">
              <span class="styleFormField__label styleFormField__label--required">展示方式</span>
              <el-form-item prop="displayMode" label-width="0" class="styleFormField__item">
                <style-form-choice
                  v-model="ruleForm.displayMode"
                  :options="displayModeOptions"
                  aria-label="展示方式"
                />
              </el-form-item>
            </div>
            <div class="styleFormField">
              <span class="styleFormField__label styleFormField__label--required">标题显示</span>
              <el-form-item prop="isShowTitle" label-width="0" class="styleFormField__item">
                <style-form-choice
                  v-model="ruleForm.isShowTitle"
                  :options="showTitleOptions"
                  aria-label="标题显示"
                />
              </el-form-item>
            </div>
            <div class="styleFormField styleFormField--full">
              <span class="styleFormField__label styleFormField__label--required">网格行列数</span>
              <el-form-item prop="columNum" label-width="0" class="styleFormField__item">
                <div class="styleFormGridStepperRow">
                  <workout-number-stepper
                    :value="gridStepperValue"
                    :min="1"
                    :max="12"
                    :step="1"
                    aria-label="网格行列数"
                    @input="onGridStepperInput"
                  />
                  <p class="styleFormField__preview styleFormField__preview--inline">
                    预览：<strong>{{ formatGridSize(ruleForm.columNum) }}</strong> 网格
                  </p>
                </div>
              </el-form-item>
            </div>
          </div>
        </el-card>
      </el-form>
    </div>

    <div slot="footer" class="styleFormDialogShell__footer">
      <template v-if="isReadonly">
        <el-button class="actionButton actionButtonGhost" size="small" @click="handleClose">
          关闭
        </el-button>
        <el-button
          class="actionButton actionButtonQuery"
          type="primary"
          size="small"
          v-check-role
          @click="$emit('edit')"
        >
          编辑
        </el-button>
      </template>
      <template v-else>
        <el-button class="actionButton actionButtonGhost" size="small" @click="handleClose">
          取消
        </el-button>
        <el-button
          class="actionButton actionButtonQuery"
          type="primary"
          size="small"
          @click="handleSubmit"
        >
          保存
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import StyleFormChoice from './StyleFormChoice.vue'
import WorkoutNumberStepper from '../../resourceManage/genericResourceManage/form/basicForms/WorkoutNumberStepper.vue'
import {
  DISPLAY_MODE_OPTIONS,
  formatGridSize,
  getDisplayModeLabel,
  getShowTitleLabel,
  isShowTitleEnabled,
} from './styleListHelpers'

export default {
  name: 'StyleFormDialog',
  components: {
    StyleFormChoice,
    WorkoutNumberStepper,
  },
  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    /** 0 创建，1 查看，2 编辑 */
    mode: { type: Number, default: 0 },
    ruleForm: { type: Object, required: true },
    rules: { type: Object, required: true },
    styleTypeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      displayModeOptions: DISPLAY_MODE_OPTIONS,
      showTitleOptions: [
        { label: '显示', value: '1' },
        { label: '不显示', value: '0' },
      ],
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(next) {
        this.$emit('update:visible', next)
      },
    },
    isReadonly() {
      return this.mode === 1
    },
    dialogTitle() {
      if (this.mode === 0) return '新建样式'
      if (this.mode === 1) return '查看样式'
      return '编辑样式'
    },
    modeBadgeText() {
      if (this.mode === 0) return '新建'
      if (this.mode === 1) return '只读'
      return '编辑中'
    },
    modeBadgeClass() {
      if (this.mode === 0) return 'styleFormDialogShell__badge--create'
      if (this.mode === 1) return 'styleFormDialogShell__badge--view'
      return 'styleFormDialogShell__badge--edit'
    },
    gridStepperValue() {
      const parsed = Number(this.ruleForm.columNum)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
    },
  },
  watch: {
    visible(next) {
      if (next && !this.isReadonly) {
        this.$nextTick(() => {
          if (this.$refs.ruleForm) {
            this.$refs.ruleForm.clearValidate()
          }
        })
      }
    },
    mode() {
      if (this.visible && !this.isReadonly) {
        this.$nextTick(() => {
          if (this.$refs.ruleForm) {
            this.$refs.ruleForm.clearValidate()
          }
        })
      }
    },
  },
  methods: {
    formatGridSize,
    getDisplayModeLabel,
    getShowTitleLabel,
    isShowTitleEnabled,
    onGridStepperInput(value) {
      this.ruleForm.columNum = value == null ? '' : String(value)
    },
    handleBeforeClose(done) {
      this.$emit('close')
      done()
    },
    handleClose() {
      this.$emit('close')
      this.innerVisible = false
    },
    handleSubmit() {
      if (!this.$refs.ruleForm) {
        this.$emit('submit')
        return
      }
      this.$refs.ruleForm.validate((valid) => {
        if (valid) {
          this.$emit('submit')
        } else {
          this.$message.info('请完整填写表单')
        }
      })
    },
  },
}
</script>

<style scoped>
.styleFormField__item {
  margin-bottom: 0;
}

.styleFormField__item >>> .el-form-item__content {
  line-height: normal;
  min-height: 0;
}

.styleFormField__item >>> .el-form-item__error {
  position: static;
  margin-top: 6px;
  padding-top: 0;
}
</style>
